import React, { useRef, useState, useEffect, useCallback } from 'react';
import authService from '../services/AuthService';

const API_BASE = 'https://server.multisellerpro.com/api';
const CHUNK_INTERVAL_MS = 6000;
const MIN_PART_BYTES = 5.1 * 1024 * 1024;

const fmt = (s) =>
    `${Math.floor(s / 60).toString().padStart(2, '0')}:${(Math.round(s) % 60).toString().padStart(2, '0')}`;

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

export default function VideoEditor({ review, onClose, onSaved }) {

    const [phase, setPhase] = useState('loading');
    const [videoBlobUrl, setVideoBlobUrl] = useState('');
    const [duration, setDuration] = useState(0);
    const [trimStart, setTrimStart] = useState(0);
    const [trimEnd, setTrimEnd] = useState(0);
    const [speed, setSpeed] = useState(1);
    const [currentTime, setCurrentTime] = useState(0);
    const [partsUploaded, setPartsUploaded] = useState(0);
    const [errorMsg, setErrorMsg] = useState('');
    const [dragging, setDragging] = useState(null); // 'start' | 'end' | null

    const videoRef = useRef(null);
    const timelineRef = useRef(null);
    const stopCheckRef = useRef(null);
    const partsRef = useRef([]);
    const partNumRef = useRef(1);
    const accBlobsRef = useRef([]);
    const accSizeRef = useRef(0);
    const queueRef = useRef([]);
    const uploadingRef = useRef(false);
    const stoppedRef = useRef(false);
    const finalisedRef = useRef(false);
    const allChunksRef = useRef([]);
    const uploadMetaRef = useRef(null);
    const recorderRef = useRef(null);

    // ── init: fetch presign + download blob ───────────────────────────────────
    useEffect(() => {
        (async () => {
            try {
                const r = await authService.authenticatedFetch(`${API_BASE}/video/overdub-presign`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ s3_key: review.mp4_key || review.s3_key }),
                });
                if (!r.ok) throw new Error();
                const { play_url, new_key, upload_id } = await r.json();
                uploadMetaRef.current = { new_key, upload_id };

                const res = await fetch(play_url);
                const blob = await res.blob();
                setVideoBlobUrl(URL.createObjectURL(blob));
                setPhase('idle');
            } catch {
                setErrorMsg('Failed to load video. Please try again.');
                setPhase('error');
            }
        })();
        return () => {
            clearInterval(stopCheckRef.current);
            if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
        };
    }, []);

    useEffect(() => () => { if (videoBlobUrl) URL.revokeObjectURL(videoBlobUrl); }, [videoBlobUrl]);

    // ── video metadata loaded ─────────────────────────────────────────────────
    const handleMetadata = () => {
        const d = videoRef.current?.duration || 0;
        setDuration(d);
        setTrimStart(0);
        setTrimEnd(d);
    };

    // ── timeline time update ──────────────────────────────────────────────────
    const handleTimeUpdate = () => {
        setCurrentTime(videoRef.current?.currentTime || 0);
    };

    // ── drag logic ────────────────────────────────────────────────────────────
    const timeFromMouseX = (clientX) => {
        const rect = timelineRef.current.getBoundingClientRect();
        const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        return pct * duration;
    };

    const handleTimelineMouseDown = (e, handle) => {
        e.preventDefault();
        setDragging(handle);
    };

    useEffect(() => {
        if (!dragging) return;

        const onMove = (e) => {
            const t = timeFromMouseX(e.clientX);
            if (dragging === 'start') {
                setTrimStart(Math.min(t, trimEnd - 0.5));
            } else {
                setTrimEnd(Math.max(t, trimStart + 0.5));
            }
        };
        const onUp = () => setDragging(null);

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, [dragging, trimStart, trimEnd, duration]);

    // ── touch drag support ────────────────────────────────────────────────────
    const handleTouchMove = (e, handle) => {
        e.preventDefault();
        const t = timeFromMouseX(e.touches[0].clientX);
        if (handle === 'start') setTrimStart(Math.min(t, trimEnd - 0.5));
        else setTrimEnd(Math.max(t, trimStart + 0.5));
    };

    // ── preview ───────────────────────────────────────────────────────────────
    const handlePreview = () => {
        const vid = videoRef.current;
        if (!vid) return;
        clearInterval(stopCheckRef.current);
        vid.currentTime = trimStart;
        vid.playbackRate = speed;
        vid.play();
        setPhase('previewing');

        stopCheckRef.current = setInterval(() => {
            if (!videoRef.current) return;
            if (videoRef.current.currentTime >= trimEnd) {
                videoRef.current.pause();
                clearInterval(stopCheckRef.current);
                setPhase('idle');
            }
        }, 100);
    };

    const stopPreview = () => {
        clearInterval(stopCheckRef.current);
        videoRef.current?.pause();
        setPhase('idle');
    };

    // ── upload helpers (identical to VideoOverdub) ────────────────────────────
    const apiPresign = async (upload_id, key, part_number) => {
        const r = await authService.authenticatedFetch(`${API_BASE}/video/presign-part`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ upload_id, key, part_number }),
        });
        if (!r.ok) throw new Error('presign-part failed');
        return r.json();
    };

    const apiComplete = async (upload_id, key, parts) => {
        const r = await authService.authenticatedFetch(`${API_BASE}/video/complete-upload`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ upload_id, key, parts }),
        });
        if (!r.ok) throw new Error('complete-upload failed');
        return r.json();
    };

    const finalise = useCallback(async () => {
        if (finalisedRef.current) return;
        finalisedRef.current = true;
        try {
            const { upload_id, new_key } = uploadMetaRef.current;
            await apiComplete(upload_id, new_key, partsRef.current);
            setPhase('saving');
            const sr = await authService.authenticatedFetch(`${API_BASE}/video/overdub-commit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    review_id: review.id,
                    original_key: review.mp4_key || review.s3_key,
                    new_key,
                }),
            });
            if (!sr.ok) throw new Error();
            const { s3_url } = await sr.json();
            setPhase('saved');
            onSaved?.(s3_url);
        } catch {
            finalisedRef.current = false;
            setPhase('error');
            setErrorMsg('Failed to save. Please try again.');
        }
    }, [review.id, review.s3_key, onSaved]);

    const drainQueue = useCallback(async () => {
        if (uploadingRef.current) return;
        uploadingRef.current = true;
        while (queueRef.current.length > 0) {
            const blob = queueRef.current.shift();
            const pn = partNumRef.current++;
            const { upload_id, new_key } = uploadMetaRef.current;
            let ok = false;
            for (let attempt = 0; attempt < 3 && !ok; attempt++) {
                try {
                    const { presigned_url } = await apiPresign(upload_id, new_key, pn);
                    const s3r = await fetch(presigned_url, {
                        method: 'PUT', body: blob, headers: { 'Content-Type': '' },
                    });
                    if (!s3r.ok) throw new Error();
                    partsRef.current.push({ PartNumber: pn, ETag: s3r.headers.get('ETag') });
                    setPartsUploaded(partsRef.current.length);
                    ok = true;
                } catch {
                    if (attempt < 2) await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
                }
            }
            if (!ok) {
                setPhase('error');
                setErrorMsg('Upload failed. Please try again.');
                uploadingRef.current = false;
                return;
            }
        }
        uploadingRef.current = false;
        if (stoppedRef.current && queueRef.current.length === 0) finalise();
    }, [finalise]);

    const flushAccumulator = useCallback((isFinal = false) => {
        if (accBlobsRef.current.length === 0) return;
        if (!isFinal && accSizeRef.current < MIN_PART_BYTES) return;
        const mimeType = recorderRef.current?.mimeType || 'video/webm';
        const blob = new Blob(accBlobsRef.current, { type: mimeType });
        accBlobsRef.current = [];
        accSizeRef.current = 0;
        queueRef.current.push(blob);
        drainQueue();
    }, [drainQueue]);

    // ── encode + upload ───────────────────────────────────────────────────────
    const handleEncode = async () => {
        // reset upload state
        partsRef.current = [];
        partNumRef.current = 1;
        accBlobsRef.current = [];
        accSizeRef.current = 0;
        queueRef.current = [];
        allChunksRef.current = [];
        uploadingRef.current = false;
        stoppedRef.current = false;
        finalisedRef.current = false;
        setPartsUploaded(0);
        setErrorMsg('');
        setPhase('encoding');

        const vid = videoRef.current;
        vid.currentTime = trimStart;
        vid.playbackRate = speed;
        vid.muted = false;

        await new Promise(r => setTimeout(r, 80)); // small settle

        const stream = vid.captureStream();
        const mimeType = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm']
            .find(m => MediaRecorder.isTypeSupported(m)) || 'video/webm';

        const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 2_500_000 });
        recorderRef.current = recorder;

        recorder.ondataavailable = (e) => {
            if (!e.data?.size || stoppedRef.current) return;
            allChunksRef.current.push(e.data);
            accBlobsRef.current.push(e.data);
            accSizeRef.current += e.data.size;
            flushAccumulator(false);
        };

        recorder.onstop = () => {
            stoppedRef.current = true;
            vid.pause();
            flushAccumulator(true);
            if (queueRef.current.length === 0 && !uploadingRef.current) finalise();
        };

        vid.play();
        recorder.start(CHUNK_INTERVAL_MS);

        // stop at trimEnd (accounting for playback speed)
        clearInterval(stopCheckRef.current);
        stopCheckRef.current = setInterval(() => {
            if (!videoRef.current) return;
            if (videoRef.current.currentTime >= trimEnd) {
                clearInterval(stopCheckRef.current);
                if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
            }
        }, 100);
    };

    // ── derived layout values ─────────────────────────────────────────────────
    const startPct = duration > 0 ? (trimStart / duration) * 100 : 0;
    const endPct = duration > 0 ? (trimEnd / duration) * 100 : 100;
    const currentPct = duration > 0 ? (currentTime / duration) * 100 : 0;
    const clipDuration = trimEnd - trimStart;
    const encodedDuration = clipDuration / speed;

    const isIdle = phase === 'idle';
    const isPreviewing = phase === 'previewing';
    const isEncoding = phase === 'encoding';
    const isSaving = phase === 'saving';
    const isSaved = phase === 'saved';
    const isError = phase === 'error';

    // ── render ────────────────────────────────────────────────────────────────
    return (
        <div style={s.overlay}>
            <div style={s.card}>

                {/* Header */}
                <div style={s.header}>
                    <span style={s.title}>
                        ✂️ Edit Video —{' '}
                        <em style={{ fontWeight: 400, fontSize: '11px' }}>
                            {review.review_title || review.product_title}
                        </em>
                    </span>
                    <button style={s.closeBtn} onClick={onClose}>✕</button>
                </div>

                {/* Loading */}
                {phase === 'loading' && (
                    <div style={s.videoWrap}>
                        <div style={s.spinnerWrap}>
                            <div style={s.spinner} />
                            <p style={s.spinnerLabel}>Loading video…</p>
                        </div>
                    </div>
                )}

                {/* Video player */}
                {['idle', 'previewing', 'encoding'].includes(phase) && videoBlobUrl && (
                    <div style={s.videoWrap}>
                        <video
                            ref={videoRef}
                            src={videoBlobUrl}
                            style={s.video}
                            playsInline
                            onLoadedMetadata={handleMetadata}
                            onTimeUpdate={handleTimeUpdate}
                        />
                        {isPreviewing && (
                            <div style={s.previewBadge}>▶ Preview</div>
                        )}
                        {isEncoding && (
                            <div style={s.encodingOverlay}>
                                <div style={s.spinner} />
                                <p style={s.spinnerLabel}>
                                    Encoding… {partsUploaded > 0 ? `(${partsUploaded} chunk${partsUploaded !== 1 ? 's' : ''} uploaded)` : 'buffering…'}
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/* Saving spinner */}
                {isSaving && (
                    <div style={s.videoWrap}>
                        <div style={s.spinnerWrap}>
                            <div style={s.spinner} />
                            <p style={s.spinnerLabel}>Saving…</p>
                        </div>
                    </div>
                )}

                {/* ── TRIM TIMELINE ── */}
                {(isIdle || isPreviewing) && duration > 0 && (
                    <div style={s.timelineSection}>

                        {/* Time labels row */}
                        <div style={s.timeLabels}>
                            <span style={s.timeLabel}>0:00</span>
                            <span style={{ ...s.timeLabel, color: '#14b8a6', fontWeight: '700' }}>
                                ✂️ {fmt(trimStart)} → {fmt(trimEnd)}
                                <span style={s.clipInfo}>
                                    {' '}({fmt(clipDuration)} clip → {fmt(encodedDuration)} at {speed}x)
                                </span>
                            </span>
                            <span style={s.timeLabel}>{fmt(duration)}</span>
                        </div>

                        {/* Timeline track */}
                        <div
                            ref={timelineRef}
                            style={s.track}
                            onClick={(e) => {
                                // clicking the track seeks the video
                                const t = timeFromMouseX(e.clientX);
                                if (videoRef.current) videoRef.current.currentTime = t;
                            }}
                        >
                            {/* Dimmed outside selected range */}
                            <div style={{ ...s.dimmed, left: 0, width: `${startPct}%` }} />
                            <div style={{ ...s.dimmed, left: `${endPct}%`, right: 0 }} />

                            {/* Selected range highlight */}
                            <div style={{
                                ...s.selectedRange,
                                left: `${startPct}%`,
                                width: `${endPct - startPct}%`,
                            }} />

                            {/* Playhead */}
                            <div style={{ ...s.playhead, left: `${currentPct}%` }} />

                            {/* Start handle */}
                            <div
                                style={{ ...s.handle, left: `${startPct}%` }}
                                onMouseDown={(e) => handleTimelineMouseDown(e, 'start')}
                                onTouchMove={(e) => handleTouchMove(e, 'start')}
                            >
                                <div style={s.handleBar} />
                                <div style={s.handleLabel}>{fmt(trimStart)}</div>
                            </div>

                            {/* End handle */}
                            <div
                                style={{ ...s.handle, left: `${endPct}%`, transform: 'translateX(-100%)' }}
                                onMouseDown={(e) => handleTimelineMouseDown(e, 'end')}
                                onTouchMove={(e) => handleTouchMove(e, 'end')}
                            >
                                <div style={s.handleBar} />
                                <div style={{ ...s.handleLabel, right: 0, left: 'auto' }}>{fmt(trimEnd)}</div>
                            </div>
                        </div>

                        <p style={s.trackHint}>Drag handles to trim · Click track to seek</p>
                    </div>
                )}

                {/* ── SPEED SELECTOR ── */}
                {(isIdle || isPreviewing) && (
                    <div style={s.speedSection}>
                        <span style={s.speedLabel}>Speed</span>
                        <div style={s.speedRow}>
                            {SPEEDS.map(sp => (
                                <button
                                    key={sp}
                                    onClick={() => setSpeed(sp)}
                                    style={speed === sp ? { ...s.speedBtn, ...s.speedBtnActive } : s.speedBtn}
                                >
                                    {sp}x
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Error */}
                {isError && <div style={s.errBox}>{errorMsg}</div>}

                {/* Saved */}
                {isSaved && (
                    <div style={s.savedWrap}>
                        <div style={s.doneCheck}>✓</div>
                        <p style={s.savedLabel}>Video saved successfully!</p>
                        <button style={{ ...s.btn, ...s.btnBlue }} onClick={onClose}>Done</button>
                    </div>
                )}

                {/* Controls */}
                {!isSaved && !isSaving && (
                    <div style={s.controls}>
                        {isIdle && (
                            <>
                                <button style={{ ...s.btn, ...s.btnTeal }} onClick={handlePreview}>
                                    ▶ Preview Clip
                                </button>
                                <button style={{ ...s.btn, ...s.btnRed }} onClick={handleEncode}>
                                    ✂️ Save Edit
                                </button>
                            </>
                        )}
                        {isPreviewing && (
                            <button style={{ ...s.btn, ...s.btnGray }} onClick={stopPreview}>
                                ■ Stop Preview
                            </button>
                        )}
                        {isEncoding && (
                            <button style={{ ...s.btn, ...s.btnGray }} disabled>
                                ⏳ Encoding…
                            </button>
                        )}
                        {isError && (
                            <button style={{ ...s.btn, ...s.btnGray }} onClick={() => setPhase('idle')}>
                                ↺ Try Again
                            </button>
                        )}
                    </div>
                )}

            </div>
            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            `}</style>
        </div>
    );
}

const s = {
    overlay: {
        position: 'fixed', inset: 0, zIndex: 10001, background: 'rgba(0,0,0,0.85)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(8px)', padding: '16px',
    },
    card: {
        background: 'linear-gradient(145deg,#0f172a 0%,#1e293b 100%)',
        borderRadius: '20px', border: '1px solid rgba(255,255,255,0.1)',
        boxShadow: '0 32px 64px rgba(0,0,0,0.6)',
        width: '100%', maxWidth: '560px', overflow: 'hidden',
        display: 'flex', flexDirection: 'column', maxHeight: '92vh', overflowY: 'auto',
    },
    header: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)',
        flexShrink: 0,
    },
    title: { color: 'white', fontWeight: '700', fontSize: '13px', fontFamily: 'sans-serif' },
    closeBtn: {
        background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white',
        width: '26px', height: '26px', borderRadius: '50%', cursor: 'pointer', fontSize: '12px',
        flexShrink: 0,
    },
    videoWrap: {
        position: 'relative', width: '100%', height: '320px',
        background: '#000', overflow: 'hidden', flexShrink: 0,
    },
    video: { width: '100%', height: '100%', objectFit: 'contain', display: 'block' },
    spinnerWrap: {
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: '12px',
    },
    encodingOverlay: {
        position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.65)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: '12px',
    },
    spinner: {
        width: '36px', height: '36px', borderRadius: '50%',
        border: '3px solid rgba(255,255,255,0.15)',
        borderTop: '3px solid #14b8a6',
        animation: 'spin 0.8s linear infinite',
    },
    spinnerLabel: { color: 'white', fontFamily: 'sans-serif', fontSize: '13px', margin: 0 },
    previewBadge: {
        position: 'absolute', top: '8px', left: '8px',
        background: 'rgba(20,184,166,0.85)', color: 'white',
        padding: '3px 10px', borderRadius: '20px',
        fontSize: '11px', fontWeight: '700', fontFamily: 'sans-serif',
    },

    // ── Timeline ──────────────────────────────────────────────────────────────
    timelineSection: {
        padding: '14px 16px 4px', flexShrink: 0,
    },
    timeLabels: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '8px',
    },
    timeLabel: {
        color: 'rgba(255,255,255,0.45)', fontSize: '10px',
        fontFamily: 'monospace', userSelect: 'none',
    },
    clipInfo: {
        color: 'rgba(255,255,255,0.4)', fontWeight: '400', fontSize: '10px',
    },
    track: {
        position: 'relative', width: '100%', height: '44px',
        background: 'rgba(255,255,255,0.08)', borderRadius: '6px',
        cursor: 'crosshair', overflow: 'visible', userSelect: 'none',
    },
    dimmed: {
        position: 'absolute', top: 0, bottom: 0,
        background: 'rgba(0,0,0,0.5)', zIndex: 1,
        pointerEvents: 'none',
    },
    selectedRange: {
        position: 'absolute', top: 0, bottom: 0,
        background: 'rgba(20,184,166,0.25)',
        border: '1px solid rgba(20,184,166,0.6)',
        zIndex: 2, pointerEvents: 'none',
    },
    playhead: {
        position: 'absolute', top: '-4px', bottom: '-4px',
        width: '2px', background: 'white',
        zIndex: 5, pointerEvents: 'none',
        transform: 'translateX(-1px)',
        boxShadow: '0 0 4px rgba(255,255,255,0.6)',
    },
    handle: {
        position: 'absolute', top: '-6px', bottom: '-6px',
        width: '18px', zIndex: 6,
        cursor: 'ew-resize', display: 'flex', alignItems: 'stretch',
        transform: 'translateX(-50%)',
    },
    handleBar: {
        width: '4px', margin: 'auto',
        background: '#14b8a6', borderRadius: '3px',
        boxShadow: '0 0 6px rgba(20,184,166,0.8)',
        height: '100%',
    },
    handleLabel: {
        position: 'absolute', bottom: '-20px', left: 0,
        color: '#14b8a6', fontSize: '9px',
        fontFamily: 'monospace', whiteSpace: 'nowrap',
        fontWeight: '700',
    },
    trackHint: {
        color: 'rgba(255,255,255,0.25)', fontSize: '10px',
        fontFamily: 'sans-serif', textAlign: 'center',
        marginTop: '20px', marginBottom: '0',
    },

    // ── Speed ─────────────────────────────────────────────────────────────────
    speedSection: {
        padding: '14px 16px 4px', flexShrink: 0,
        borderTop: '1px solid rgba(255,255,255,0.06)',
    },
    speedLabel: {
        color: 'rgba(255,255,255,0.45)', fontSize: '10px',
        fontFamily: 'sans-serif', textTransform: 'uppercase',
        letterSpacing: '1px', display: 'block', marginBottom: '8px',
    },
    speedRow: {
        display: 'flex', gap: '6px', flexWrap: 'wrap',
    },
    speedBtn: {
        padding: '5px 12px', borderRadius: '50px', border: 'none',
        background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)',
        fontSize: '11px', fontWeight: '600', cursor: 'pointer',
        fontFamily: 'sans-serif', transition: 'all 0.15s',
    },
    speedBtnActive: {
        background: 'linear-gradient(90deg,#14b8a6,#0d9488)',
        color: 'white', boxShadow: '0 0 8px rgba(20,184,166,0.5)',
    },

    // ── Controls / Saved / Error ──────────────────────────────────────────────
    controls: {
        display: 'flex', gap: '8px', padding: '14px 16px',
        justifyContent: 'center', flexWrap: 'wrap', flexShrink: 0,
        borderTop: '1px solid rgba(255,255,255,0.06)',
    },
    btn: {
        padding: '9px 22px', borderRadius: '50px', border: 'none',
        fontWeight: '700', fontSize: '12px', cursor: 'pointer',
        fontFamily: 'sans-serif',
    },
    btnRed: { background: 'linear-gradient(90deg,#f43f5e,#e11d48)', color: 'white' },
    btnGray: { background: 'rgba(255,255,255,0.14)', color: 'white' },
    btnTeal: { background: 'linear-gradient(90deg,#14b8a6,#0d9488)', color: 'white' },
    btnBlue: { background: 'linear-gradient(90deg,#2563eb,#1d4ed8)', color: 'white' },
    errBox: {
        margin: '8px 16px', background: 'rgba(244,63,94,0.12)',
        border: '1px solid rgba(244,63,94,0.35)', color: '#fda4af',
        borderRadius: '8px', padding: '8px 12px', fontSize: '12px', fontFamily: 'sans-serif',
    },
    savedWrap: {
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        gap: '10px', padding: '24px 16px',
    },
    doneCheck: {
        width: '56px', height: '56px', borderRadius: '50%', background: '#14b8a6',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '28px', color: 'white', fontWeight: '700',
    },
    savedLabel: { color: 'white', fontWeight: '600', fontSize: '14px', margin: 0, fontFamily: 'sans-serif' },
};